import { useEffect, useState } from "react";
import useLabels from "../../hooks/useLabels";
import { useSelector } from "react-redux";
import { selectConfigs } from "../../context/selectors";
import { addDays, format, isBefore, isEqual } from "date-fns";
import LotLabelTags from "../LotLabelTags";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const MultipleRowsCell = ({ getValue, row, column, table }) => {
  const columnMeta = column.columnDef.meta;
  const selector = columnMeta?.selector || "";
  const [data, setData] = useState([]);
  const [getLabel] = useLabels();
  const configs = useSelector(selectConfigs);

  const getRightUnit = (isName = false) => {
    const unit_id = row.original?.unit_id || null;
    if (!row.original?.ingredients || !unit_id) return [];

    switch(unit_id) {
      case 1:
      case 2:
      case 4:
      case 6:
      case 7:
        // Kg
        if (isName) return "Kg";
        return 4;
      case 3:
        // L
        if (isName) return "L";
        return 3;
      case 5:
        // unit
        if (isName) return "Unit";
        return 5;
    }

    return 5;
  }

  const getRightValue = (v) => {
    switch (selector) {
      case "cost":
      case "candle_cost": return `${v?.cost || '-'} €`;
      case "drug_batch_number": return `${v?.drug_batch_number || '-'} (${v?.quantity || '-'} ${v?.warehouse_unit || ""})`;
      case "porosity": return `${v?.porosity || '-'} µm [${v?.porosity ? getLabel(`filterCandleType_${v?.type}`)?.toLowerCase?.() : ''}]`;
      case "candle_total": return `${Number(v?.total_filtered_liters) || 0}${v?.max_capacity_liters ? '/' : ''}${Number(v?.max_capacity_liters) || ''} L`;

      default: return v?.[selector];
    }
  }

  useEffect(() => {
    let tmp = [];

    if (selector) {
      switch (selector) {
        case "candle_cost":
          const candleArray = row?.original?.[columnMeta?.array] || [];
          const totalCandleCost = candleArray?.reduce((acc, curr) => acc + (Number(curr?.cost) || 0), 0);
          tmp.push({ candle_cost: totalCandleCost > 0 ? (totalCandleCost + ' €') : "" });
          break;
        case "type":
        case "supplier":
        case "batch_unitary_cost":
        case "serial_number":
        case "porosity":
        case "candle_total":
        case "batch_number":
        case "batch_quantity":
        case "expire_date":
        case "drug_batch_number":
          const stockArray = row?.original?.[columnMeta?.array] || [];  
          if (selector === "batch_number") {
            stockArray?.forEach(s => 
              // tmp.push({ batch_number: s?.batch_number, additional: `(x${s?.stock || '0'})` }));
              tmp.push({ batch_number: s?.batch_number }));
          } else if (selector === "batch_unitary_cost") {
            const perUnit = row?.original?.unit_id ? getLabel(`drugBatchUnit_${getRightUnit()}`) : '';
            stockArray?.forEach(s => 
              tmp.push({ batch_unitary_cost: `${s?.cost || '-'} € ${perUnit}`})
            );
          } else if (["serial_number", "porosity", "candle_cost", "candle_total", "drug_batch_number"].includes(selector)) {
            stockArray?.forEach(s => 
              tmp.push({ [selector]: getRightValue(s) })
            );
          } else if (selector === "type") {
            if (stockArray?.length === 1 || (stockArray?.length === 0 && row?.original?.type === 'DETERGENT')) tmp.push({ type: getLabel(`drugType_${row?.original?.type}`) })
            else {
              const types = new Set();
              stockArray?.reduce((_, item) => types.add(item?.type), null);
              Array.from(types)?.filter(Boolean)?.forEach(t => 
                tmp.push({ type: getLabel(`drugType_${t}`) })
              );
            }
          } else if (selector === "batch_quantity") {
            const perUnit = row?.original?.unit_id ? getLabel(`drugBatchUnit_${getRightUnit(true)}`) : '';
            stockArray?.forEach(s => 
              tmp.push({ batch_quantity: s?.stock ? `${s?.stock?.toFixed?.(3)} ${perUnit}` : ''})
            );
          }
          else if (selector === "expire_date") {
            stockArray?.forEach(s => 
              tmp.push({ expire_date: format(new Date(s?.expire_date) || null, configs.shortDateFormat)})
            );
          }
          else
            tmp.push(...stockArray);
          break;
        case "ingredients":
          const ingredientsArray = row?.original?.[columnMeta?.array];
          if (ingredientsArray && ingredientsArray?.length > 0) {
            const sortedIngrByQuantity = [...ingredientsArray]?.sort((a, b) => b.quantity - a.quantity);
            sortedIngrByQuantity?.forEach(i => {
              const ingredients = i?.name || "";
              const ingredientQuantity = `(${i.quantity}%)`
              tmp.push({ ingredients: ingredients, additional: ingredientQuantity });
            });
          }
          break;
        case "ranges":
          const rangesArray = row?.original?.[columnMeta?.array];
          if (rangesArray && rangesArray?.length > 0) {
            rangesArray?.forEach(r => {
              // if just one color, no need to explict the corresponding ranges 
              const colors = r?.colors?.length > 0 && rangesArray?.length > 1 ? r?.colors?.reduce((accumulator, current) => {
                return accumulator + (accumulator ? ', ' : '') + getLabel(`lotColor_${current}`);
              }, '') : null;
              const colorsString = colors ? `[${colors}]` : '';
              tmp.push({
                ranges: `${r?.min_range ? String(r?.min_range) : 'n/a'} - ${r?.max_range ? String(r?.max_range) : 'n/a'} ${r?.unit || ""}`,
                additional: `${colorsString}`
              });
            });
          }
          break;
        case "states":
          const statesArray = row?.original?.[columnMeta?.array];
          if (statesArray && statesArray?.length > 0) {
            statesArray?.forEach((row, index) => {
              if (index > 0) return false;
              const states = row?.states?.reduce((accumulator, current) => {
                return accumulator + (accumulator ? ', ' : '') + getLabel(`lotState_${current}`);
              }, '');
              tmp.push({
                states: '',
                additional: states
              });
            });
          }
          break;
        case "client_names":
          const clientNamesArray = row?.original?.[columnMeta?.array];
          if (clientNamesArray) {
            tmp = clientNamesArray?.map((v) => {
              return {
                [selector]: v,
              };
            }); 
            break;
          }
          break;
        case "unit":
        case "substance_name":
        case "quantity_unit":
          let array = row?.original[columnMeta?.array];
          if (selector === 'substance_name' && columnMeta?.array === 'analysis') {
            array = array?.map((t) => { return {...t, substance_name: getLabel("substance_" + t?.substance_name) }});
          }
          else if (selector === 'quantity_unit') {
            array = array?.map((t) => { return {...t, quantity_unit: selector === "quantity_unit" ? `${t?.quantity} ${t?.unit || ""}`: ""}});
          }
          
          if (array) {
            tmp = array;
            break;
          }
        case "usernames":
          const usernameArray = row?.original[columnMeta?.array];
          if (usernameArray) {
            tmp = usernameArray;
            break;
          }
        case "varieties":
        case "origins":
        case "years":
          const varArray = row?.original[columnMeta?.array];
          if (varArray) {
            tmp = varArray?.map((v) => {
              return {
                [selector]: v,
              };
            });
            break;
          }
        case "aggregate":
          const additional = [];
          const lot = row?.original;
          const compositions = lot?.composition || [];
          const contributions = {};

          // Process destination's existing wine (old composition)
          compositions.forEach((comp) => {
            const { variety, year, origin, percentage } = comp;
            const key = `${variety}|${year}|${origin}`;
            const contribution = lot?.actual_quantity * (percentage / 100);
            
            contributions[key] = (contributions[key] || 0) + contribution;
          });
        
          // Convert contributions to final percentages
          let i = 0;
          let total = 0;
          for (const key in contributions) {
            const [variety, yearStr, origin] = key.split("|");
            const contribution = contributions[key];
            const percentage = (contribution / lot?.actual_quantity) * 100;
            const v = variety ? getLabel(`lotVarieties_${variety}`) : '';
            const y = yearStr ? ` [${yearStr}]` : '';
            const o = origin ? `, ${origin}` : '';
            let roundedPerc = Math.round(percentage ?? 0);

            // If it's the last item, adjust so the total is exactly 100
            if (i === Object.keys(compositions).length - 1) {
              roundedPerc = 100 - total;
            } else {
              total += roundedPerc;
            }
          
            const p = percentage != null ? ` (${roundedPerc}%)` : '';
            additional.push(`${v}${p}${o}${y}`);
            i += 1;
          }

          // let total = 0;
          // for (let i = 0; i < compositions.length; i++) {
          //   const c = compositions[i];
          //   const v = c?.variety ? getLabel(`lotVarieties_${c.variety}`) : '';
          //   const y = c?.year ? ` [${c.year}]` : '';
          //   const o = c?.origin ? `, ${c.origin}` : '';
          //   let roundedPerc = Math.round(c?.percentage ?? 0);

          //   // If it's the last item, adjust so the total is exactly 100
          //   if (i === compositions.length - 1) {
          //     roundedPerc = 100 - total;
          //   } else {
          //     total += roundedPerc;
          //   }
          
          //   const p = c?.percentage != null ? ` (${roundedPerc}%)` : '';
          //   additional.push(`${v}${p}${o}${y}`);
          // }
          // for (let i = 0; i < lot?.varieties?.length; i++) {
          //   const v = lot?.varieties?.[i] ? getLabel(`lotVarieties_${lot?.varieties?.[i]}`) : '';
          //   const y = lot?.years?.[i] ? ` [${lot?.years?.[i]}]` : '';
          //   const o = lot?.origins?.[i] ? `, ${lot?.origins?.[i]}` : '';
          //   const p = lot?.percentages?.length > 1 ? ` (${lot?.percentages?.[i]}%)` : "";
          //   additional.push(`${v}${p}${o}${y}`);
          // }
          if (additional) {
            tmp = additional?.map((v) => {
              return {
                [selector]: v,
              };
            });
            break;
          }
        case "lot_label":
          if (Array.isArray(columnMeta?.array)) {
            const labels = columnMeta?.array?.find(l => row.original?.batch_id === l?.id) || null;
            if (labels && labels?.label) tmp = labels?.label || [];
          } else tmp = row?.original?.label || [];
          break;
        case "tank_name":
          const tanks = row?.original[columnMeta?.array] || [];
          if (tanks) {
            tmp = tanks?.map((v) => {
              return {
                [selector]: v?.[selector],
                additional: v?.additional
              };
            })?.sort((a, b) => (a?.tank_name)?.localeCompare(b?.tank_name, undefined, {numeric: true}));
            break;
          }
        default:
          break;
      }
    }

    setData([...tmp]);
  }, [row.original]);

  const parseDateDMY = (dateStr) => {
    if (!dateStr) return null;
    const [day, month, yearStr] = dateStr.split('/');
  
    let year = Number(yearStr);
    if (yearStr.length === 2) {
      year = year < 50 ? 2000 + year : 1900 + year;
    }
  
    return new Date(year, Number(month) - 1, Number(day));
  };  

  const renderExpireIcons = (expireDateStr) => {
    if (!expireDateStr) return null;
    const expireDate = parseDateDMY(expireDateStr);
    if (!expireDate) return null;
    const today = new Date();
    const warningThreshold = addDays(today, 14); // oggi + 14 giorni
    
    if (isBefore(expireDate, today) || isEqual(expireDate, today)) {
      // Scaduto o scadenza oggi -> Unicode "!"
      return (
        <span className="additional icon" title={columnMeta?.additionalErrorMessage || 'Prodotto scaduto'}>
          &#10071;
        </span>
      );
    } else if (isBefore(expireDate, warningThreshold) || isEqual(expireDate, warningThreshold)) {
      // Scadenza entro 14 giorni -> warning FontAwesome
      return (
        <span className="additional icon" title={columnMeta?.additionalWarningMessage || 'Scadenza imminente'}>
          <FontAwesomeIcon icon={"fa-triangle-exclamation"} color="orange" size="lg" />
        </span>
      );
    }

    return null; // nessuna icona se oltre i 14 giorni
  };


  const getValueByParam = (r) => {
    if (!r || typeof selector !== "string" || !(selector in r)) {
      return <span></span>;
    }
  
    const value = r[selector];
    const hasValue = value !== null && value !== undefined && value !== "";

    return (
      <span>
        {hasValue ? value : "\u00A0\u00A0"}
        {r?.additional && (
          <span className="additional_span">
            {hasValue && "\u00A0\u00A0"}
            {r.additional}
          </span>
        )}
        {(columnMeta?.additionalType === "expired" && selector === "expire_date") && renderExpireIcons(value)}
      </span>
    );
  };
  
  if (selector === "lot_label") return <div className="lots_tags"><LotLabelTags tags={data} color={data?.color?.toLowerCase?.()} /></div>

  return (
    <div>
      {data?.map?.((r, index) => {
        return (
          <div
            key={`multiple_row_${r?.index || r?.substance_id || r[selector] || index}_${Math.floor(Math.random() * 10000)}`}
          >
            {getValueByParam(r)}
          </div>
        );
      })}
    </div>
  );
};

export default MultipleRowsCell;
